<script setup lang="ts">
import { DueState } from '@/api/generator';
import { computed } from 'vue';
import { lightThemeColors } from '@/styles/vuetify';
import { DateTime, Duration } from 'luxon';
import { useNow } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

interface DueProps {
  state?: DueState;
  startDate?: DateTime | null;
  endDate?: DateTime | null;
}
const props = withDefaults(defineProps<DueProps>(), {
  state: DueState.NotDue,
  startDate: null,
  endDate: null,
});

const nowDate = useNow({ interval: 60000 });
const now = computed(() => DateTime.fromJSDate(nowDate.value));

function getDueInLabel(start: DateTime | null): string | undefined {
  // if start is not defined do not render badge
  if (!start) return undefined;

  const fullTimeFormat = start.diff(now.value, ['days', 'hours', 'minutes']);
  // if it’s in the past or completed do not render badge
  if (fullTimeFormat.as('minutes') <= 0) {
    return undefined;
  }

  // if it’s 7 days or more away do not render badge
  if (fullTimeFormat.as('days') >= 7) {
    return undefined;
  }

  let duration: Duration;

  // render only days in whole numbers
  if (fullTimeFormat.as('days') >= 1) {
    const wholeDays = Math.ceil(fullTimeFormat.as('days'));
    duration = Duration.fromObject({ days: wholeDays });

    // render hours and mins in whole numbers
  } else if (fullTimeFormat.as('hours') >= 1) {
    const totalMins = Math.floor(fullTimeFormat.as('minutes'));
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    duration = Duration.fromObject({ hours: hrs, minutes: mins });

    // render only minutes in whole numbers
  } else {
    const mins = Math.floor(fullTimeFormat.as('minutes'));
    duration = Duration.fromObject({ minutes: mins });
  }

  // format the return with toHuman()
  const durationText = duration.toHuman({ unitDisplay: 'short' });

  if (!durationText) return undefined;

  return t('NotDue', '', { durationText });
}

const badgeLabel = computed<string | undefined>(() => {
  switch (props.state) {
    case DueState.Overdue:
      return t('Overdue');

    case DueState.Due:
      return t('Due');

    case DueState.NotDue:
      return getDueInLabel(props.startDate);

    default:
      return undefined;
  }
});

function getColor(dueState: DueState) {
  const colorMap: Record<DueState, string | undefined> = {
    [DueState.NotDue]: lightThemeColors.success,
    [DueState.Due]: lightThemeColors.warning,
    [DueState.Overdue]: lightThemeColors.error,
  };
  return colorMap[dueState];
}

const color = computed(() => getColor(props.state));
</script>

<template>
  <v-chip v-if="badgeLabel" label size="small" :color="color" :text="badgeLabel ?? ''" />
</template>
